import axios, { type AxiosInstance, type InternalAxiosRequestConfig, type AxiosResponse } from 'axios';

import { useAuthStore } from '@/stores/auth.store';

const API_BASE_URL = '/api/v1';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/** Attach JWT token to every outgoing request, and fix FormData Content-Type */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const { token } = useAuthStore.getState();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // When sending FormData, remove the default 'application/json' Content-Type so
    // that the browser can auto-set 'multipart/form-data; boundary=...' correctly.
    // Without this, axios serialises FormData as JSON and the backend receives no file.
    if (config.data instanceof FormData) {
      config.headers.delete('Content-Type');
    }
    return config;
  },
  (error: unknown) => Promise.reject(error),
);

/**
 * Handle 401 responses by clearing auth state — BUT skip this behaviour for
 * the login endpoint itself, since a 401 there is an expected "invalid
 * credentials" response, not a session expiry.  Without this guard the page
 * would redirect to /login and reload before the caller's .catch() had a
 * chance to show the real error message.
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const url = error.config?.url ?? '';
      const isAuthEndpoint =
        url.includes('/auth/login') ||
        url.includes('/auth/register') ||
        url.includes('/auth/refresh') ||
        url.includes('/auth/customer-login') ||
        url.includes('/auth/customer-otp');
      if (!isAuthEndpoint) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

export { apiClient };
